// =============================================================================
// Wiki Sidebar - Navigation sidebar for the Wiki module
// =============================================================================

import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useWikiStore } from '@/modules/wiki/hooks/useWikiStore';
import { cn } from '@/lib/utils';
import {
  BookOpen, ChevronLeft, ChevronRight, Plus, Search,
  FileText, Star, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDocFavorites } from '@/modules/documents/hooks/useDocFavorites';

interface WikiSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const COLORS = {
  bg: '#FFFFFF',
  bgHover: '#EEF2FF',
  bgActive: '#E0E7FF',
  border: '#E2E8F0',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  accent: '#6366F1', // Indigo matching wiki module
};

export function WikiSidebar({ collapsed, onToggle }: WikiSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { wikiDocs, loadingWiki, loadWikiDocs } = useWikiStore();
  const { favorites } = useDocFavorites();

  useEffect(() => {
    if (currentOrganization?.id) {
      loadWikiDocs(currentOrganization.id);
    }
  }, [currentOrganization?.id]);

  const isOnWiki = location.pathname.startsWith('/wiki');
  const activeDocId = location.pathname.match(/^\/wiki\/([a-f0-9-]+)/)?.[1];

  // Filter favorites that are wiki docs
  const wikiFavorites = favorites.filter(f =>
    wikiDocs.some(d => d.id === f.document_id)
  );

  const handleCreate = () => {
    navigate('/wiki?action=create');
  };

  if (collapsed) {
    return (
      <aside
        className="flex flex-col w-12 shrink-0 border rounded-xl h-full"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        <div className="h-14 flex items-center justify-center border-b" style={{ borderColor: COLORS.border }}>
          <Button variant="ghost" size="icon" className="w-8 h-8" style={{ color: COLORS.textSecondary }} onClick={onToggle}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="flex flex-col items-center py-3 gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate('/wiki')}
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                  style={{ backgroundColor: isOnWiki && !activeDocId ? COLORS.bgActive : 'transparent' }}
                >
                  <BookOpen className="h-4 w-4" style={{ color: COLORS.accent }} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Tous les wikis</TooltipContent>
            </Tooltip>
            {wikiDocs.map(doc => (
              <Tooltip key={doc.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(`/wiki/${doc.id}`)}
                    className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors text-sm"
                    style={{ backgroundColor: activeDocId === doc.id ? COLORS.bgActive : 'transparent' }}
                  >
                    {doc.icon}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{doc.title}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  return (
    <aside
      className="flex flex-col w-56 shrink-0 border rounded-xl h-full"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b" style={{ borderColor: COLORS.border }}>
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="h-4 w-4 shrink-0" style={{ color: COLORS.accent }} />
          <span className="font-semibold text-sm truncate" style={{ color: COLORS.textPrimary }}>Wiki</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="icon" className="w-7 h-7 shrink-0" style={{ color: COLORS.textSecondary }} onClick={onToggle}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="w-7 h-7 shrink-0" style={{ color: COLORS.textSecondary }}>
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuItem onClick={handleCreate} className="text-xs gap-2">
                <BookOpen className="h-3.5 w-3.5" />
                Nouveau wiki
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {/* All wikis link */}
        <nav className="p-2 space-y-0.5">
          <button
            onClick={() => navigate('/wiki')}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-[12px] transition-colors cursor-pointer text-left"
            style={{
              backgroundColor: isOnWiki && !activeDocId ? COLORS.bgActive : 'transparent',
              color: isOnWiki && !activeDocId ? COLORS.accent : COLORS.textPrimary,
              fontWeight: isOnWiki && !activeDocId ? 500 : 400,
            }}
            onMouseEnter={(e) => {
              if (!(isOnWiki && !activeDocId)) e.currentTarget.style.backgroundColor = '#F1F5F9';
            }}
            onMouseLeave={(e) => {
              if (!(isOnWiki && !activeDocId)) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <BookOpen className="h-4 w-4 shrink-0" style={{ color: isOnWiki && !activeDocId ? COLORS.accent : COLORS.textSecondary }} />
            <span className="truncate flex-1">Tous les wikis</span>
          </button>
        </nav>

        {/* Separator */}
        <div className="mx-3 my-2 border-t" style={{ borderColor: COLORS.border }} />

        {/* Wiki documents list */}
        <div className="px-2 space-y-0.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: COLORS.textSecondary }}>
            Pages Wiki
          </p>
          {loadingWiki ? (
            <div className="flex justify-center py-4">
              <div className="h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : wikiDocs.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 flex flex-col items-center text-center mx-1" style={{ borderColor: '#E2E8F0' }}>
              <BookOpen className="h-5 w-5 mb-1.5" style={{ color: COLORS.accent }} />
              <p className="text-[11px] leading-tight" style={{ color: COLORS.textSecondary }}>
                Créez un wiki ou transformez un document existant
              </p>
            </div>
          ) : (
            wikiDocs.map(doc => (
              <button
                key={doc.id}
                onClick={() => navigate(`/wiki/${doc.id}`)}
                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-[12px] transition-colors text-left"
                style={{
                  backgroundColor: activeDocId === doc.id ? COLORS.bgActive : 'transparent',
                  color: activeDocId === doc.id ? COLORS.accent : COLORS.textPrimary,
                  fontWeight: activeDocId === doc.id ? 500 : 400,
                }}
                onMouseEnter={(e) => {
                  if (activeDocId !== doc.id) e.currentTarget.style.backgroundColor = '#F1F5F9';
                }}
                onMouseLeave={(e) => {
                  if (activeDocId !== doc.id) e.currentTarget.style.backgroundColor = activeDocId === doc.id ? COLORS.bgActive : 'transparent';
                }}
              >
                <span className="text-sm shrink-0">{doc.icon}</span>
                <span className="truncate flex-1">{doc.title}</span>
              </button>
            ))
          )}
        </div>

        {/* Favoris Wiki */}
        {wikiFavorites.length > 0 && (
          <>
            <div className="mx-3 my-2 border-t" style={{ borderColor: COLORS.border }} />
            <div className="px-3 pb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: COLORS.textSecondary }}>
                <Star className="h-3 w-3" style={{ fill: '#FBBF24', color: '#FBBF24' }} />
                Favoris
              </p>
              <div className="space-y-0.5">
                {wikiFavorites.map(fav => (
                  <button
                    key={fav.id}
                    onClick={() => navigate(`/wiki/${fav.document_id}`)}
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-[12px] transition-colors text-left hover:bg-muted/50"
                    style={{ color: COLORS.textPrimary }}
                  >
                    <span className="text-sm shrink-0">{fav.document_icon}</span>
                    <span className="truncate flex-1">{fav.document_title}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t" style={{ borderColor: COLORS.border }}>
        <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
          📚 Base de connaissances
        </p>
      </div>
    </aside>
  );
}
